import { ApiService } from "../utils/ApiService.js";
import { AuthService } from "./AuthService.js";
import { AccesosService } from "./AccesosService.js";
import { moneda, fecha, textoVencimiento, capitalizar } from "../utils/Formato.js";

// IdTipoBien: mismos códigos que ID_BIEN_X_TIPO de FenixSeg.
// `etiqueta` es el encabezado de la tarjeta cuando el back no manda `Etiqueta`. Antes ese respaldo
// salía de `Ramo`, que el back no tiene cargado; con el tipo de bien alcanza y no depende de nada.
const TIPOS_BIEN = {
  1: { clave: "auto", icono: "fa-car-side", tituloBien: "Vehículo asegurado", etiqueta: "PÓLIZA AUTO" },
  2: { clave: "hogar", icono: "fa-house-chimney", tituloBien: "Inmueble asegurado", etiqueta: "PÓLIZA HOGAR" },
  3: { clave: "personal", icono: "fa-user-shield", tituloBien: "Persona asegurada", etiqueta: "PÓLIZA PERSONAL" },
};
const TIPO_BIEN_DEFAULT = {
  clave: "auto",
  icono: "fa-file-shield",
  tituloBien: "Bien asegurado",
  etiqueta: "PÓLIZA",
};

// Etiquetas y orden de los campos del objeto `Bien`, que cambia de forma según el ramo.
// Una clave que no esté acá igual se muestra (humanizada) y va al final: que el back sume
// campos nuevos no debe requerir tocar el front.
const ETIQUETAS_BIEN = {
  Descripcion: "Descripción",
  Marca: "Marca",
  Modelo: "Modelo",
  Ano: "Año",
  Dominio: "Patente",
  Motor: "Motor",
  Chasis: "Chasis",
  Uso: "Uso",
  GNC: "GNC",
  Direccion: "Dirección",
  EntreCalles: "Entre calles",
  CodigoPostal: "Código postal",
  Provincia: "Provincia",
  Partido: "Partido",
  Localidad: "Localidad",
  TipoUso: "Tipo de uso",
  Actividad: "Actividad",
  FechaNacimiento: "Fecha de nacimiento",
  Beneficiarios: "Beneficiarios",
};
const ORDEN_BIEN = Object.keys(ETIQUETAS_BIEN);

// Los campos largos ocupan la fila entera; el resto va de a dos por fila (como la ficha de la ref).
const CAMPOS_ANCHOS = new Set(["Descripcion", "Direccion", "EntreCalles", "Beneficiarios", "Modelo"]);

const humanizar = (clave) => clave.replace(/([a-z])([A-Z])/g, "$1 $2");

const valorLegible = (clave, valor) => {
  if (valor === null || valor === undefined || valor === "") return "—";
  if (typeof valor === "boolean") return valor ? "Sí" : "No";
  if (/^Fecha/.test(clave)) return fecha(valor);
  return String(valor);
};

const campo = (etiqueta, valor, ancho = false) => ({
  Etiqueta: etiqueta,
  Valor: valor === null || valor === undefined || valor === "" ? "—" : String(valor),
  // 2 columnas también en mobile (como la ficha de referencia); los campos largos van a fila entera.
  Clase: ancho ? "col-12" : "col-6",
});

const claseEstado = (p) => {
  if (!p.Vigente) return "estado--vencida";
  if (p.CuotasImpagas > 0) return "estado--alerta";
  return "estado--vigente";
};

export class PolizasService {
  /** Credenciales del home (slider). */
  static async listar({ forceRefresh = false } = {}) {
    const resp = await ApiService.get("polizas", AuthService.getToken(), {
      // Sin vencimiento: la frescura la decide el sello del back, no el reloj. Ver EstadoService.
      cacheTiempo: "permanente",
      forceRefresh,
    });
    if (resp?.status !== 1) throw new Error(resp?.message || "No pudimos obtener tus pólizas.");
    return (resp.message || []).map((p) => PolizasService.aCredencial(p));
  }

  /** Aplana una póliza del listado al shape que consume la tarjeta del slider. */
  static aCredencial(p) {
    const tipo = TIPOS_BIEN[p.IdTipoBien] || TIPO_BIEN_DEFAULT;
    return {
      Id: p.Id,
      NumeroPoliza: p.NumeroPoliza,
      Etiqueta: p.Etiqueta || tipo.etiqueta,
      Titulo: p.Titulo,
      Subtitulo: p.Subtitulo,
      Identificador: p.Identificador,
      IdentificadorEtiqueta: p.IdentificadorEtiqueta || "Identificador",
      Compania: p.Compania,
      Plan: p.Plan,
      Icono: tipo.icono,
      Tema: "cred--" + tipo.clave,
      Vigente: !!p.Vigente,
      VigenciaHastaTexto: fecha(p.VigenciaHasta),
      VencimientoTexto: textoVencimiento(p.VigenciaHasta),
      EstadoTexto: p.Estado,
      EstadoClase: claseEstado(p),
      TieneImpagas: (p.CuotasImpagas || 0) > 0,
      ImpagasTexto:
        (p.CuotasImpagas || 0) > 0
          ? p.CuotasImpagas + (p.CuotasImpagas === 1 ? " cuota impaga" : " cuotas impagas")
          : "",
    };
  }

  /** Detalle completo, ya formateado para el template. */
  static async obtener(id, { forceRefresh = false } = {}) {
    const resp = await ApiService.get("polizas/" + id, AuthService.getToken(), {
      // Sin vencimiento: la frescura la decide el sello del back, no el reloj. Ver EstadoService.
      cacheTiempo: "permanente",
      forceRefresh,
    });
    if (resp?.status !== 1) throw new Error(resp?.message || "No encontramos la póliza.");
    return PolizasService.aDetalle(resp.message);
  }

  static aDetalle(p) {
    const tipo = TIPOS_BIEN[p.IdTipoBien] || TIPO_BIEN_DEFAULT;
    const credencial = PolizasService.aCredencial(p);

    const seccionPoliza = {
      Titulo: "Información de póliza",
      Icono: "fa-file-contract",
      Campos: [
        campo("Mi plan", p.Plan, true),
        campo("Compañía", p.Compania),
        campo("Número de póliza", p.NumeroPoliza),
        campo("Cobertura", p.TipoCobertura, true),
        campo("Vigencia desde", fecha(p.VigenciaDesde)),
        campo("Vigencia hasta", fecha(p.VigenciaHasta)),
        campo("Suma asegurada", moneda(p.SumaAsegurada)),
        campo("Premio total", moneda(p.Premio)),
        campo("Valor de cuota", moneda(p.Cuota)),
        campo("Cantidad de cuotas", p.CantidadCuotas),
        campo("Forma de pago", p.FormaPago),
        campo("Detalle de pago", p.FormaPagoDetalle),
      ].filter((c) => c.Valor !== "—"),
    };

    const bien = p.Bien || {};
    const clavesBien = [
      ...ORDEN_BIEN.filter((k) => k in bien),
      ...Object.keys(bien).filter((k) => !ORDEN_BIEN.includes(k)),
    ];
    const seccionBien = {
      Titulo: tipo.tituloBien,
      Icono: tipo.icono,
      Campos: clavesBien.map((k) =>
        campo(ETIQUETAS_BIEN[k] || humanizar(k), valorLegible(k, bien[k]), CAMPOS_ANCHOS.has(k))
      ),
    };

    const t = p.Titular || {};
    const seccionTitular = {
      Titulo: "Titular de la póliza",
      Icono: "fa-user",
      Campos: [
        campo("Nombre y apellido", capitalizar((t.Nombre || "") + " " + (t.Apellido || "")).trim(), true),
        campo(t.TipoDocumento || "Documento", t.Documento),
        campo("Teléfono", t.Telefono),
        campo("Correo electrónico", t.Email, true),
      ].filter((c) => c.Valor !== "—"),
    };

    const cuotas = (p.Cuotas || []).map((c) => ({
      Numero: c.Numero,
      ImporteTexto: moneda(c.Importe),
      VencimientoTexto: fecha(c.FechaVencimiento),
      Pagada: !!c.Pagada,
      EstadoTexto: c.Pagada ? "Pagada " + fecha(c.FechaPago) : "Impaga",
      EstadoClase: c.Pagada ? "cuota--pagada" : "cuota--impaga",
    }));

    // La asistencia es de la compañía y depende del ramo (auxilio mecánico, asistencia hogar…):
    // por eso vive en la póliza y no en el menú del home.
    const asistencia = p.Asistencia || {};
    const telAsistencia = asistencia.Telefono ? String(asistencia.Telefono).replace(/[^\d+]/g, "") : "";

    // El back manda solo NombreDocumento y UrlDocumento (sin tipo ni fecha): el ícono sale de la
    // extensión de la URL.
    const documentos = (p.Documentos || [])
      .map((d) => ({
        Nombre: d.NombreDocumento || "Documento",
        Url: d.UrlDocumento || "",
        Icono: /\.pdf(\?|$)/i.test(d.UrlDocumento || "") ? "fa-file-pdf" : "fa-file-lines",
      }))
      // Un documento sin URL sería un link a ninguna parte.
      .filter((d) => d.Url);

    return {
      ...credencial,
      Secciones: [seccionPoliza, seccionBien, seccionTitular].filter((s) => s.Campos.length > 0),
      Coberturas: p.Coberturas || [],
      HayCoberturas: (p.Coberturas || []).length > 0,
      Cuotas: cuotas,
      HayCuotas: cuotas.length > 0,
      CuotasImpagas: cuotas.filter((c) => !c.Pagada).length,
      Documentos: documentos,
      HayDocumentos: documentos.length > 0,
      Asistencia: asistencia,
      TieneAsistencia: !!telAsistencia,
      AsistenciaTitulo: asistencia.Titulo || "Asistencia 24hs",
      AsistenciaTelefonoTexto: asistencia.Telefono || "",
      AsistenciaTelHref: telAsistencia ? "tel:" + telAsistencia : "",
      // El productor es siempre FenixSeg: sale de configuración, no del JSON de la póliza.
      ProductorWaHref: AccesosService.whatsappContacto(
        "Quiero consultar por mi póliza " + (p.NumeroPoliza || "") + "."
      ),
    };
  }
}
